import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames/bind";
import { withTranslation } from "../translation/";
import { ERROR_TYPE } from "../../state/modules/error";
import { ERROR_ORIGIN } from "./constants";
import TouchOutside from "../TouchOutside";
import Escapable from "../Escapable";
import styles from "./ErrorAlert.css";
import getErrorKeys from "./CopyProvider/index";

const styleContext = classNames.bind(styles);

const getContainerClasses = type =>
  type === ERROR_TYPE.network
    ? {
        networkError: true
      }
    : {};

export const ErrorAlert = ({
  error,
  isLoading,
  undoSearch,
  clearError,
  retrySearch,
  formatTranslation
}) => {
  const [currentError, setCurrentError] = useState(error ? error : null);
  const { type: errorType, errorOrigin, actionType } = currentError || {};
  const buttonElement = useRef();

  useEffect(() => {
    if (error) {
      setCurrentError(error);
    }
  }, [error]);

  useEffect(() => {
    if (isVisible() && buttonElement.current) {
      buttonElement.current.focus();
    }
  }, [error, isLoading]);

  const isServerError = () => errorType === ERROR_TYPE.server;

  const getButtonElement = () => buttonElement.current;

  const isVisible = () => !!error && (!isServerError() || !isLoading);

  const getErrorButtonContent = () => (
    <>
      <Escapable escapeHandler={getOutsideEscapeHandler()} />
      <TouchOutside
        getElement={getButtonElement}
        outsideHandler={getOutsideEscapeHandler()}
      />
      <div className={styles.content}>
        <p className={styles.message}>
          <span className={styles.header}>
            {formatTranslation(getErrorKeys(errorOrigin, errorType).header)}
          </span>
          <span className={styles.details}>
            {formatTranslation(getErrorKeys(errorOrigin, errorType).details)}
          </span>
        </p>
        {errorOrigin === ERROR_ORIGIN.search ? (
          <button
            className={styles.retryButton}
            onClick={getClickHandler()}
            tabIndex={isVisible() ? 0 : -1}
            aria-label={formatTranslation("plp_web_errors_retry")}
          >
            {formatTranslation("plp_web_errors_retry")}
          </button>
        ) : (
          <button
            className={styles.close}
            onClick={getClickHandler()}
            aria-label={formatTranslation("accessibility_label_close")}
          >
            ×
          </button>
        )}
      </div>
    </>
  );

  const outsideEscapeHandler = {
    [ERROR_ORIGIN.search]: () => {
      if (isServerError()) {
        clickHandler[ERROR_ORIGIN.search]();

        return;
      }

      undoSearch();
    },
    [ERROR_ORIGIN.saveItem]: () => {
      clearError();
    }
  };

  const clickHandler = {
    [ERROR_ORIGIN.search]: () => {
      retrySearch({ actionType, forceReapplyFacets: true });
    },
    [ERROR_ORIGIN.saveItem]: () => {
      clearError();
    }
  };

  const getOutsideEscapeHandler = () =>
    outsideEscapeHandler[currentError?.errorOrigin] || clearError;

  const getClickHandler = () =>
    clickHandler[currentError?.errorOrigin] || clearError;

  const containerClasses = getContainerClasses(errorType);

  return (
    <div
      className={styleContext({
        errorAlert: true,
        visible: isVisible(),
        ...containerClasses
      })}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      ref={buttonElement}
      tabIndex={-1}
    >
      {getErrorButtonContent()}
    </div>
  );
};

ErrorAlert.propTypes = {
  error: PropTypes.shape({
    message: PropTypes.string.isRequired,
    type: PropTypes.number.isRequired,
    errorOrigin: PropTypes.string.isRequired,
    actionType: PropTypes.string.isRequired
  }),
  formatTranslation: PropTypes.func.isRequired,
  undoSearch: PropTypes.func.isRequired,
  retrySearch: PropTypes.func.isRequired,
  clearError: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired
};

ErrorAlert.displayName = "ErrorAlert";

export default withTranslation(ErrorAlert);
